// src/components/dashboard/GetNewAccountCard.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import { useAuth } from '../../contexts/AuthContext';

const GetNewAccountCard = ({ OnCreateNewAccount, user }) => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
    const { currentUser, currentAccountDetails, existingAccount, refreshUserData, logout } = useAuth();
  const [isCheckingBusiness, setIsCheckingBusiness] = useState(false);
  const [businessNameStatus, setBusinessNameStatus] = useState(''); // 'available', 'taken', 'registered', 'checking'
  const [businessNameError, setBusinessNameError] = useState('');
  const [isBusinessRegistered, setIsBusinessRegistered] = useState(false);
  const [filePreview, setFilePreview] = useState(null);

  const [formData, setFormData] = useState({
    countryCode: '+234',
    phoneNumber: '',
    companyName: ''
  });

  const [verificationData, setVerificationData] = useState({
    rc_number: '',
    business_certificate: null,
    ownsBusiness: false
  });

  const businessCheckTimeout = useRef(null);
  const companyNameInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const countryCodes = [
    { code: '+234', country: 'NG', flag: '🇳🇬' },
    { code: '+233', country: 'GH', flag: '🇬🇭' },
    { code: '+254', country: 'KE', flag: '🇰🇪' },
    { code: '+27', country: 'ZA', flag: '🇿🇦' },
    { code: '+1', country: 'US', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' }
  ];

  // Constants
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

  // Business status configurations
  const BUSINESS_STATUS_CONFIG = {
    checking: { color: 'blue', icon: 'spinner', message: 'Checking business name availability...' },
    available: { color: 'green', icon: 'check', message: 'Business name is available' },
    registered: { color: 'yellow', icon: 'warning', message: 'This business name is registered with CAC.' },
    error: { color: 'red', icon: 'error', message: 'Error checking business name. Please try again.' }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (businessCheckTimeout.current) {
        clearTimeout(businessCheckTimeout.current);
      }
    };
  }, []);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFormData({
        countryCode: '+234',
        phoneNumber: user.company_phone || '',
        companyName: user?.company_name || user.fullname || ''
      });
    }
  }, [user]);

  // Function to remove leading zeros from phone number
  const removeLeadingZeros = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all leading zeros
    let cleanedNumber = phoneNumber.replace(/^0+/, '');
    
    // If the number becomes empty after removing zeros, return the original
    if (cleanedNumber === '') {
      return phoneNumber;
    }
    
    return cleanedNumber;
  };

  // Function to clean phone number for submission
  const cleanPhoneNumberForSubmission = (phoneNumber, countryCode) => {
    if (!phoneNumber) return '';
    
    // Remove all non-digit characters first
    let cleaned = phoneNumber.replace(/\D/g, '');
    
    // Remove leading zeros
    cleaned = removeLeadingZeros(cleaned);
    
    // Remove country code if it's already included in the number
    const countryCodeDigits = countryCode.replace('+', '');
    if (cleaned.startsWith(countryCodeDigits)) {
      cleaned = cleaned.substring(countryCodeDigits.length);
    }
    
    return cleaned;
  };

  // Memoized form validation - FIXED VERSION
  const isFormReady = useCallback(() => {
    const hasBasicInfo = formData.companyName.trim() && formData.phoneNumber.trim();
    
    // If business check hasn't been triggered yet, allow submission
    if (!businessNameStatus) {
      return hasBasicInfo;
    }
    
    // If business check is in progress, don't allow submission
    if (businessNameStatus === 'checking' || isCheckingBusiness) {
      return false;
    }

    // If business is registered, check verification requirements
    if (businessNameStatus === 'registered') {
      return verificationData.ownsBusiness && 
             verificationData.rc_number.trim() && 
             verificationData.business_certificate;
    }

    // For available or other statuses, allow submission with basic info
    return hasBasicInfo;
  }, [formData, businessNameStatus, isCheckingBusiness, verificationData]);

  // Input change handler - only updates form data, doesn't trigger API calls
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setVerificationData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'companyName') {
      setFormData(prev => ({ ...prev, [name]: value }));
      // Reset status when user starts typing new content
      if (businessNameStatus && !value.includes(formData.companyName)) {
        setBusinessNameStatus('');
        setBusinessNameError('');
        setIsBusinessRegistered(false);
      }
    } else if (name === 'phoneNumber') {
      // Allow only numbers and keep original formatting for display
      const numbersOnly = value.replace(/[^\d]/g, '');
      setFormData(prev => ({ ...prev, [name]: numbersOnly }));
    } else if (name === 'countryCode') {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setVerificationData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle blur event - trigger business check when user leaves the field
  const handleCompanyNameBlur = () => {
    if (formData.companyName.trim().length > 2) {
      checkBusinessNameAvailability(formData.companyName.trim());
    }
  };

  // Business name availability check
  const checkBusinessNameAvailability = async (businessName) => {
    if (!businessName || businessName.length < 3) return;

    setIsCheckingBusiness(true);
    setBusinessNameStatus('checking');

    try {
      const response = await api.post(API_CONFIG.AUTH.IS_REGISTERED_BUSINESS, {
        businessName,
        userId: user?.id
      });

      const data = response.data;

      if (data.isRegistered) {
        setBusinessNameStatus('registered');
        setBusinessNameError('This business name is registered with CAC. If you own this business, please verify ownership below.');
        setIsBusinessRegistered(true);
      } else {
        setBusinessNameStatus('available');
        setBusinessNameError('');
        setIsBusinessRegistered(false);
      }
    } catch (error) {
      console.error('Business check error:', error);
      setBusinessNameStatus('error');
      setBusinessNameError('Error checking business name. Please try again.');
      setIsBusinessRegistered(false);
    } finally {
      setIsCheckingBusiness(false);
    }
  };

  // File handling
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (file.size > MAX_FILE_SIZE) {
      showToast('File size must be less than 10MB', 'error');
      return;
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      showToast('Please upload a PDF, JPG, or PNG file', 'error');
      return;
    }

    setVerificationData(prev => ({ ...prev, business_certificate: file }));
    createFilePreview(file);
  };

  // Handle blur event on phone number field - also trigger business check
  const handlePhoneNumberBlur = () => {
    if (formData.companyName.trim().length > 2 && !businessNameStatus) {
      checkBusinessNameAvailability(formData.companyName.trim());
    }
  };

  const createFilePreview = (file) => {
    if (file.type === 'application/pdf') {
      setFilePreview({
        type: 'pdf',
        name: file.name,
        size: file.size
      });
    } else if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview({
          type: 'image',
          url: e.target.result,
          name: file.name,
          size: file.size
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setVerificationData(prev => ({ ...prev, business_certificate: null }));
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Clean the phone number by removing leading zeros and existing country code
      const cleanedPhoneNumber = cleanPhoneNumberForSubmission(formData.phoneNumber, formData.countryCode);
      

      const submissionData = {
        phoneNumber: cleanedPhoneNumber,
        countryCode: formData.countryCode.replace('+', ''), // Remove + from country code
        companyName: formData.companyName,
        userId: user?.id,
        ownsBusiness: verificationData.ownsBusiness,
        ...(isBusinessRegistered && verificationData.ownsBusiness && {
          rc_number: verificationData.rc_number,
          business_certificate: verificationData.business_certificate
        })
      };

      await OnCreateNewAccount(submissionData);
      
      // Only reset if not requiring verification
      if (!isBusinessRegistered || !verificationData.ownsBusiness) {
        handleCloseModal();
      }
    } catch (error) {
      console.error('Error generating account:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    if (!formData.companyName.trim()) {
      showToast('Please enter a company name', 'error');
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      showToast('Please enter a phone number', 'error');
      return false;
    }
    if (businessNameStatus === 'checking' || isCheckingBusiness) {
      showToast('Please wait for business name verification to complete', 'error');
      return false;
    }
    if (businessNameStatus === 'registered' && !verificationData.ownsBusiness) {
      showToast('Please confirm business ownership to continue', 'error');
      return false;
    }
    if (businessNameStatus === 'registered' && verificationData.ownsBusiness && (!verificationData.rc_number.trim() || !verificationData.business_certificate)) {
      showToast('RC number and business certificate are required for registered businesses', 'error');
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setFormData({ 
      countryCode: '+234',
      phoneNumber: '', 
      companyName: '' 
    });
    setVerificationData({ rc_number: '', business_certificate: null, ownsBusiness: false });
    setBusinessNameStatus('');
    setBusinessNameError('');
    setIsBusinessRegistered(false);
    setIsCheckingBusiness(false);
    setFilePreview(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Status icon renderer
  const renderStatusIcon = () => {
    const status = BUSINESS_STATUS_CONFIG[businessNameStatus];
    if (!status) return null;

    switch (status.icon) {
      case 'spinner':
        return <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />;
      case 'check':
        return (
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getInputStyles = () => {
    const baseStyles = "w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 transition-colors";
    
    if (isCheckingBusiness) {
      return `${baseStyles} bg-gray-100 border-gray-300 cursor-not-allowed`;
    }

    const status = BUSINESS_STATUS_CONFIG[businessNameStatus];
    if (status) {
      // Use template literals with actual color classes
      if (status.color === 'green') {
        return `${baseStyles} border-green-300 bg-green-50`;
      } else if (status.color === 'yellow') {
        return `${baseStyles} border-yellow-300 bg-yellow-50`;
      } else if (status.color === 'red') {
        return `${baseStyles} border-red-300 bg-red-50`;
      } else if (status.color === 'blue') {
        return `${baseStyles} border-blue-300 bg-blue-50`;
      }
    }

    return `${baseStyles} border-gray-300`;
  };

  return (
    <>
      {/* Generate Account Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 hover:shadow-md transition-shadow duration-300">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          
          <h3 className="text-xl font-semibold text-gray-900 mb-3">
            Account Upgrade Required
          </h3>
          
          <p className="text-gray-600 mb-6 leading-relaxed">
            Effective <b>November 7th</b>, all existing businesses are required to upgrade to a new account with enhanced security protocols.
            <span className="block mt-1"><b>Important:</b> This upgrade will generate a new account number for your business.</span>
          </p>
          
          <button
            onClick={() => setShowModal(true)}
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
          >
            Complete Business Profile
          </button>
          <button className="ml-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white px-8 py-3 rounded-xl hover:from-gray-700 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium" onClick={() => {logout()}}>
            Logout
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col lg:flex-row shadow-xl">
            {/* Left Side - Business Information */}
            <div className="flex-1 p-8 border-r border-gray-200 overflow-y-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Business Profile Setup
                  </h3>
                  <p className="text-gray-600 text-sm mt-1">
                    Complete your business information to generate your account
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-semibold text-gray-700 mb-3">
                    Company / Business Name *
                  </label>
                  <div className="relative">
                    <input
                      ref={companyNameInputRef}
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      onBlur={handleCompanyNameBlur}
                      className={getInputStyles()}
                      placeholder="Enter your Company / Business Name"
                      required
                    />
                    <div className="absolute right-3 top-3">
                      {renderStatusIcon()}
                    </div>
                  </div>
                  
                  {businessNameStatus && (
                    <p className={`text-${BUSINESS_STATUS_CONFIG[businessNameStatus]?.color}-600 text-sm mt-3 flex items-center font-medium`}>
                      {BUSINESS_STATUS_CONFIG[businessNameStatus]?.message}
                    </p>
                  )}
                  {businessNameStatus === 'registered' && (
                    <p className="text-yellow-700 text-sm mt-2 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                      {businessNameError}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700 mb-3">
                    Whatsapp Number *
                  </label>
                  <div className="flex space-x-2">
                    <div className="relative w-24">
                      <select
                        name="countryCode"
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none bg-white"
                        value={formData.countryCode}
                        onChange={handleInputChange}
                      >
                        {countryCodes.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        onBlur={handlePhoneNumberBlur}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter your whatsapp number"
                        required
                      />
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-2">
                    Add your whatsapp number to receive instant updates and product requests.
                  </p>
                </div>

                <div className="flex space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!isFormReady() || isLoading}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm hover:shadow-md"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3" />
                        Processing...
                      </div>
                    ) : (
                      'Generate Account'
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Right Side - Business Verification */}
            {isBusinessRegistered && (
              <div className="lg:w-96 bg-gradient-to-b from-yellow-50 to-yellow-100 border-l border-yellow-200 p-8 overflow-y-auto">
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4 border border-yellow-200">
                      <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-yellow-900 text-lg mb-2">Business Verification Required</h4>
                    <p className="text-yellow-800 text-sm leading-relaxed">
                      This business name is registered with CAC. Please verify ownership to prevent fraud and ensure account security.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <label className="flex items-start space-x-3 p-4 bg-white rounded-xl border-2 border-yellow-300 hover:border-yellow-400 transition-colors shadow-sm">
                      <input
                        type="checkbox"
                        name="ownsBusiness"
                        checked={verificationData.ownsBusiness}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5 flex-shrink-0"
                      />
                      <span className="text-sm text-yellow-900 leading-relaxed">
                        I confirm that I am the legal owner or authorized representative of this registered business entity
                      </span>
                    </label>

                    {verificationData.ownsBusiness && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-yellow-900 mb-3">
                            RC Number *
                          </label>
                          <input
                            type="text"
                            name="rc_number"
                            value={verificationData.rc_number}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border-2 border-yellow-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white transition-colors"
                            placeholder="Enter your RC number"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-yellow-900 mb-3">
                            Business Certificate *
                          </label>
                          
                          {!filePreview ? (
                            <div 
                              className="border-2 border-dashed border-yellow-300 rounded-xl p-8 text-center cursor-pointer hover:border-yellow-400 transition-colors bg-white hover:bg-yellow-50"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <svg className="w-12 h-12 text-yellow-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                              <p className="text-yellow-800 font-medium mb-2">Upload Business Certificate</p>
                              <p className="text-yellow-600 text-sm">PDF, JPG, PNG (Max 10MB)</p>
                            </div>
                          ) : (
                            <div className="bg-white rounded-xl border-2 border-yellow-300 p-4">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-semibold text-yellow-900">Uploaded Document</span>
                                <button
                                  type="button"
                                  onClick={removeFile}
                                  className="text-yellow-600 hover:text-yellow-800 transition-colors p-1"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                              
                              {filePreview.type === 'pdf' && (
                                <div className="flex items-center space-x-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                  <div className="w-12 h-16 bg-red-500 rounded-lg flex items-center justify-center shadow-sm">
                                    <span className="text-white text-xs font-bold">PDF</span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-yellow-900 truncate">{filePreview.name}</p>
                                    <p className="text-xs text-yellow-700">{formatFileSize(filePreview.size)}</p>
                                  </div>
                                </div>
                              )}
                              
                              {filePreview.type === 'image' && (
                                <div className="space-y-3">
                                  <div className="relative bg-white rounded-lg overflow-hidden border border-yellow-200">
                                    <img 
                                      src={filePreview.url} 
                                      alt="Document preview" 
                                      className="w-full h-40 object-contain"
                                    />
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-yellow-800 truncate flex-1 mr-2 font-medium">
                                      {filePreview.name}
                                    </span>
                                    <span className="text-xs text-yellow-600 whitespace-nowrap">
                                      {formatFileSize(filePreview.size)}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          
                          <input
                            ref={fileInputRef}
                            type="file"
                            name="business_certificate"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleFileChange}
                            className="hidden"
                            required={verificationData.ownsBusiness}
                          />
                          
                          <p className="text-yellow-700 text-xs mt-3 font-medium">
                            Accepted formats: PDF, JPG, PNG (Maximum file size: 10MB)
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default GetNewAccountCard;